import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { auth } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../pages/Login.css';

/** A mesma regra do servidor (`SafetyHelper.IsStrongPassword`): para o ecrã dizer o que falta antes de o servidor recusar. */
export const passwordProblem = (value) => {
  if (!value || value.length < 8) return 'A nova password precisa de pelo menos 8 caracteres.';
  if (!/[A-Z]/.test(value)) return 'A nova password precisa de uma letra maiúscula.';
  if (!/[a-z]/.test(value)) return 'A nova password precisa de uma letra minúscula.';
  if (!/[0-9]/.test(value)) return 'A nova password precisa de um dígito.';
  return null;
};

/**
 * O formulário de troca de password. Com `forced`, é o ecrã que a pessoa vê depois de entrar com uma password temporária
 * (definida pelo administrador): não há mais nada para fazer enquanto não a trocar, e só pode terminar a sessão.
 * Sem `forced`, é a troca normal a partir das definições (pede a atual e a nova).
 */
export function ChangePasswordForm({ forced = false, onDone }) {
  const { completePasswordChange, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const problem = passwordProblem(next);
    if (problem) return setError(problem);
    if (next !== again) return setError('As duas passwords novas não coincidem.');
    if (next === current) return setError('A nova password tem de ser diferente da atual.');

    setBusy(true);
    try {
      const r = await auth.changePassword(current, next);
      // O servidor devolve um token novo (o antigo, de password temporária, só servia para esta troca).
      completePasswordChange(r.data?.token);
      toast.success('Password alterada.');
      setCurrent(''); setNext(''); setAgain('');
      if (onDone) onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível alterar a password.');
    } finally {
      setBusy(false);
    }
  };

  const field = (label, value, setValue, autoComplete, id) => (
    <div className="auth-field-group">
      <label className="auth-field-label" htmlFor={id}>{label}</label>
      <div className="auth-input-wrapper">
        <span className="auth-input-icon">🔒</span>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          className="auth-input auth-input-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete={autoComplete}
          required
        />
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="auth-form-inner" aria-label="Alterar password">
      {field(forced ? 'Password temporária (a que recebeu)' : 'Password atual', current, setCurrent, 'current-password', 'pwd-current')}
      {field('Nova password', next, setNext, 'new-password', 'pwd-new')}
      {field('Repetir a nova password', again, setAgain, 'new-password', 'pwd-again')}
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '4px 0 12px', cursor: 'pointer' }}>
        <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Mostrar as passwords
      </label>
      <p style={{ fontSize: 12, opacity: 0.75, margin: '0 0 12px' }}>Pelo menos 8 caracteres, com maiúsculas, minúsculas e dígitos.</p>
      {error ? <div role="alert" style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</div> : null}
      <button type="submit" className="auth-submit-btn" disabled={busy}>{busy ? 'A guardar…' : 'Guardar nova password'}</button>
      {forced ? (
        <button type="button" className="auth-link-small" style={{ marginTop: 12, background: 'none', border: 0, cursor: 'pointer', textDecoration: 'underline' }} onClick={logout}>
          Terminar sessão
        </button>
      ) : null}
    </form>
  );
}

/** O ecrã inteiro da troca obrigatória (no lugar da aplicação, até a password ser trocada). */
export function ForcedPasswordChange() {
  return (
    <div className="auth-split-page">
      <div className="auth-split-right" style={{ width: '100%' }}>
        <div className="auth-form-container">
          <div className="auth-mobile-brand" style={{ display: 'flex' }}>
            <img src="/gymag-icon.svg" alt="" width="44" height="44" /><span>GyMag</span>
          </div>
          <h2 className="auth-form-title">Escolha uma nova password</h2>
          <p className="auth-form-subtitle">
            Entrou com uma password temporária. Por segurança, defina agora uma password só sua para continuar.
          </p>
          <ChangePasswordForm forced />
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordForm;

/** O botão «Alterar palavra-passe» das definições: abre/fecha o formulário no próprio sítio. */
export function ChangePasswordToggle({ label = 'Alterar Palavra-passe' }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" className="btn-secondary" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        🔒 {label}
      </button>
      {open ? (
        <div style={{ maxWidth: 420, marginTop: 16 }}>
          <ChangePasswordForm onDone={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
}
